// ============================================
// FILE: src/components/ProfileScreen.jsx
// ============================================

import React, { useState } from 'react';
import { User, MapPin, Shield, Heart, Package, Bell, LogOut, ChevronRight, MessageCircle, Smartphone, ArrowLeft, Palette, SunMedium, Trees, MoonStar, Check, QrCode, Copy, Gift, Users } from 'lucide-react';
import PWAScreen from './PWAScreen';
import QRCodeScreen from './QRCodeScreen';
import { getProductImage } from '../utils/imageHelpers';

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return String(d);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(date.getDate()).padStart(2,'0')}-${months[date.getMonth()]}-${date.getFullYear()}`;
};

const ProfileScreen = ({
  userProfile,
  setUserProfile,
  handleLogout,
  subscription,
  setSubscription,
  twoFactorEnabled,
  setTwoFactorEnabled,
  showSecuritySettings,
  setShowSecuritySettings,
  loginHistory,
  securityLogs,
  orders,
  favorites,
  setFavorites,
  setShowFavorites,
  setShowNotificationsModal,
  setShowPaymentMethods,
  setShowEditAddress,
  setShowEditProfile,  // Add this line
  addSecurityLog,
  deliveryAddresses,
  handleAddAddress,
  handleUpdateAddress,
  handleDeleteAddress,
  handleSetDefaultAddress,
  setShowAddAddress,
  editingAddress,        // Add this
  setEditingAddress,      // Add this
  allSubscriptions = [],
  handlePauseSubscription,
  handleResumeSubscription,
  handleCancelSubscription,
  handleModifySubscription,
  handleReactivateSubscription,
  appSettings,
  appTheme,
  onChangeTheme,
  loading,
  setActiveTab,
  goBack,
  walletBalance,
  onShowWalletTopup,
  onShowWalletTransactions,
  referralInfo = null,
  onApplyReferralCode,
  isDesktop = false,
}) => {
  const [showPWAScreen, setShowPWAScreen] = useState(false);
  const [showQRScreen, setShowQRScreen] = useState(false);
  const [referralCodeCopied, setReferralCodeCopied] = useState(false);
  const [applyingReferral, setApplyingReferral] = useState(false);
  const [referralInput, setReferralInput] = useState('');
  const [referralMsg, setReferralMsg] = useState(null); // { type: 'success'|'error', text }

  const copyReferralCode = (code) => {
    navigator.clipboard?.writeText(code).then(() => {
      setReferralCodeCopied(true);
      setTimeout(() => setReferralCodeCopied(false), 2000);
    });
  };

  const handleApplyReferral = async () => {
    if (!referralInput.trim() || !onApplyReferralCode) return;
    setApplyingReferral(true);
    setReferralMsg(null);
    try {
      const resp = await onApplyReferralCode(referralInput.trim().toUpperCase());
      if (resp && resp.success) {
        setReferralMsg({ type: 'success', text: resp.data?.message || 'Referral applied!' });
        setReferralInput('');
      } else {
        setReferralMsg({ type: 'error', text: resp?.data?.message || 'Failed to apply code.' });
      }
    } catch (e) {
      setReferralMsg({ type: 'error', text: 'Failed to apply referral code.' });
    } finally {
      setApplyingReferral(false);
    }
  };

  const themeOptions = [
        {
          id: 'blue',
          label: 'Blue',
          description: 'Modern blue gradients and accents.',
          icon: Palette,
          previewClass: '',
          previewIconClass: 'text-white',
        },
        {
          id: 'white',
          label: 'White',
          description: 'Minimal white and light gray surfaces.',
          icon: Palette,
          previewClass: 'from-white via-gray-100 to-gray-200',
          previewIconClass: 'text-[#535766]',
        },
        {
          id: 'sunrise',
          label: 'Sunrise',
          description: 'Very light amber, orange, and sky tones.',
          icon: SunMedium,
          previewClass: '',
          previewIconClass: 'text-white',
        },
        {
          id: 'grove',
          label: 'Grove',
          description: 'Fresh mint tones with deep evergreen accents.',
          icon: Trees,
          previewClass: '',
          previewIconClass: 'text-white',
        },
        {
          id: 'midnight',
          label: 'Midnight',
          description: 'Dark navy panels with electric blue highlights.',
          icon: MoonStar,
          previewClass: '',
          previewIconClass: 'text-slate-100',
        },
        {
          id: 'pulse',
          label: 'Pulse',
          description: 'Forest green palette with soft botanical surfaces.',
          icon: Palette,
          previewClass: 'from-[#337639] via-[#4BAB40] to-[#CFE0CF]',
          previewIconClass: 'text-white',
        },
  ];

  const renderSubCard = (sub) => {
    const statusLower = (sub.status || '').toLowerCase();
    const prod        = sub.product || {};
    const mrp         = parseFloat(prod.price        || sub.price        || 0);
    const salePrice   = parseFloat(prod.sale_price   || sub.salePrice    || mrp);
    const subPrice    = parseFloat(prod.subscription_price || sub.subscriptionPrice || salePrice);
    const discount    = sub.plan?.discount || sub.planDiscount || sub.discount || 0;
    const perDelivery = subPrice > 0 ? subPrice * (1 - discount / 100) : 0;
    const freq        = sub.planId || sub.frequency || '';
    const deliveries  = freq === 'daily' ? 30 : freq === 'alternate' ? 15 : 4;
    const totalCost   = perDelivery * deliveries;
    const totalSaving = mrp > 0 ? (mrp * deliveries) - totalCost : 0;

    const statusColors = {
      active:    'bg-[#e8f7f3] text-[#03a685]',
      paused:    'bg-yellow-100 text-yellow-700',
      cancelled: 'bg-red-100 text-red-600',
      canceled:  'bg-red-100 text-red-600',
      expired:   'bg-gray-100 text-[#94969f]',
    };
    const statusCls = statusColors[statusLower] || 'bg-gray-100 text-[#94969f]';

    return (
      <div key={sub.id || sub.subscription_number} className="bg-white rounded shadow-sm overflow-hidden mb-3">
        {/* Header — mirrors order card header */}
        <div className="bg-[#e8f7f3] p-4 border-b border-[#b7e5d8]">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-bold text-[#282c3f] text-sm">
                {sub.subscription_number || (sub.id ? `Subscription #${sub.id}` : 'Subscription')}
              </h3>
              <p className="text-xs text-[#94969f] mt-0.5">
                {sub.plan?.name || sub.planName || sub.frequency || '—'} plan
              </p>
              <p className="text-xs text-[#94969f] mt-0.5">
                📅 Started: {fmtDate(sub.startDate || sub.created_at)}
              </p>
            </div>
            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${statusCls}`}>
              {statusLower.charAt(0).toUpperCase() + statusLower.slice(1)}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="p-4">
          {/* Product row */}
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-shrink-0">{getProductImage(prod, 'large')}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[#282c3f] text-sm truncate">
                {prod.name || sub.productName || 'Subscription'}
              </p>
              {prod.description && (
                <p className="text-xs text-[#94969f] truncate mt-0.5">{prod.description}</p>
              )}
            </div>
          </div>

          {/* Pricing row — mirrors order totals */}
          {mrp > 0 && (
            <div className="border border-gray-100 rounded-lg divide-y divide-gray-100 mb-3 text-sm">
              <div className="flex justify-between px-3 py-2">
                <span className="text-[#94969f]">MRP / delivery</span>
                <span className="text-[#94969f] line-through">₹{mrp.toFixed(2)}</span>
              </div>
              <div className="flex justify-between px-3 py-2">
                <span className="text-[#94969f]">Subscription price</span>
                <span className="font-semibold text-[#ff3f6c]">₹{(perDelivery > 0 ? perDelivery : salePrice).toFixed(2)}</span>
              </div>
              {totalSaving > 0 && (
                <div className="flex justify-between px-3 py-2 bg-[#e8f7f3]">
                  <span className="text-[#03a685] font-medium">You save ({deliveries} deliveries)</span>
                  <span className="font-bold text-[#03a685]">₹{totalSaving.toFixed(2)}</span>
                </div>
              )}
            </div>
          )}

          {/* Action buttons — mirrors order actions */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
            {statusLower === 'cancelled' || statusLower === 'canceled' ? (
              <>
                <button onClick={() => handleReactivateSubscription && handleReactivateSubscription(sub.id)} className="flex-1 text-[#ff3f6c] text-xs font-medium bg-[#fff8fa] px-3 py-2 rounded-lg hover:bg-[#fff8fa] transition-all">Reactivate</button>
                <button onClick={() => typeof handleModifySubscription === 'function' && handleModifySubscription(sub)} className="flex-1 text-[#535766] text-xs font-medium bg-gray-50 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all">Modify Plan</button>
              </>
            ) : statusLower === 'paused' || statusLower === 'inactive' ? (
              <>
                <button onClick={() => handleResumeSubscription && handleResumeSubscription(sub.id)} className="flex-1 text-[#03a685] text-xs font-medium bg-[#e8f7f3] px-3 py-2 rounded-lg hover:bg-[#e8f7f3] transition-all">Resume</button>
                <button onClick={() => handleCancelSubscription && handleCancelSubscription(sub.id)} className="flex-1 text-red-600 text-xs font-medium bg-red-50 px-3 py-2 rounded-lg hover:bg-red-100 transition-all">Cancel</button>
                <button onClick={() => typeof handleModifySubscription === 'function' && handleModifySubscription(sub)} className="flex-1 text-[#535766] text-xs font-medium bg-gray-50 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all">Modify Plan</button>
              </>
            ) : (
              <>
                <button onClick={() => handlePauseSubscription && handlePauseSubscription(sub.id)} className="flex-1 text-[#ff3f6c] text-xs font-medium bg-[#fff8fa] px-3 py-2 rounded-lg hover:bg-[#fff8fa] transition-all">Pause</button>
                <button onClick={() => handleCancelSubscription && handleCancelSubscription(sub.id)} className="flex-1 text-red-600 text-xs font-medium bg-red-50 px-3 py-2 rounded-lg hover:bg-red-100 transition-all">Cancel</button>
                <button onClick={() => typeof handleModifySubscription === 'function' && handleModifySubscription(sub)} className="flex-1 text-[#535766] text-xs font-medium bg-gray-50 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all">Modify Plan</button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (showPWAScreen) {
    return <PWAScreen onBack={() => setShowPWAScreen(false)} appTheme={appTheme} />;
  }

  if (showQRScreen) {
    return <QRCodeScreen onBack={() => setShowQRScreen(false)} appTheme={appTheme} />;
  }

  return (
    <div className="pb-20 min-h-screen" style={{ background: 'var(--primary-bg)', color: 'var(--text-main)' }}>
      {/* Same bar as Shopping Bag on the cart page. The identity block below it
          moves onto white too, since the old white-on-gradient text would be
          invisible against it. */}
      <div className="fashion-scope bg-white">
        <div
          className={`flex items-center gap-3 px-4 py-3 ${isDesktop ? 'max-w-[1200px] mx-auto' : ''}`}
          style={{ borderBottom: '1px solid var(--fx-line)' }}
        >
          <button
            onClick={() => goBack ? goBack() : setActiveTab('home')}
            className="p-1 -ml-1"
            title="Go Back"
            aria-label="Go back"
          >
            <ArrowLeft size={22} style={{ color: 'var(--fx-ink)' }} />
          </button>
          <span className="fx-label flex-1">My Profile</span>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5"
            style={{ fontSize: 13, fontWeight: 700, color: 'var(--fx-pink)' }}
          >
            <LogOut size={15} />
            LOGOUT
          </button>
        </div>

        <div
          className={`flex items-center gap-4 px-4 py-5 ${isDesktop ? 'max-w-[1200px] mx-auto' : ''}`}
          style={{ borderBottom: '1px solid var(--fx-line)' }}
        >
          <div className="rounded-full overflow-hidden flex-shrink-0" style={{ width: 64, height: 64, background: '#f5f5f6' }}>
            {userProfile.profilePic ? (
              <img
                src={userProfile.profilePic}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User size={28} style={{ color: 'var(--fx-ink-3)' }} />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="truncate" style={{ fontSize: 18, fontWeight: 700, color: 'var(--fx-ink)' }}>
              {userProfile.name || 'User'}
            </h1>
            <p className="truncate" style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>
              {userProfile.email || 'user@email.com'}
            </p>
            <button
              onClick={() => setShowEditProfile(true)}
              className="mt-1"
              style={{ fontSize: 13, fontWeight: 700, color: 'var(--fx-pink)' }}
            >
              EDIT PROFILE
            </button>
          </div>
        </div>
      </div>

      <div className="p-4" style={{ color: 'var(--text-main)' }}>
        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <button
            onClick={() => setActiveTab('orders')}
            className="rounded p-4 text-center shadow-sm hover:shadow-md transition-colors cursor-pointer"
            style={{ background: 'var(--primary-bg-light)', color: 'var(--text-main)' }}
          >
            <Package size={24} style={{ color: 'var(--primary)', margin: '0 auto 0.5rem' }} />
            <p className="text-2xl font-bold">{orders ? orders.length : 0}</p>
            <p className="text-xs" style={{ color: 'var(--accent)' }}>Orders</p>
          </button>

          <button
            onClick={() => setShowFavorites(true)}
            className="rounded p-4 text-center shadow-sm hover:shadow-md transition-colors cursor-pointer"
            style={{ background: 'var(--primary-bg-light)', color: 'var(--text-main)' }}
          >
            <Heart size={24} style={{ color: 'var(--danger)', margin: '0 auto 0.5rem' }} />
            <p className="text-2xl font-bold">{favorites ? favorites.length : 0}</p>
            <p className="text-xs" style={{ color: 'var(--accent)' }}>Favorites</p>
          </button>

          <button
            onClick={() => setShowNotificationsModal(true)}
            className="rounded p-4 text-center shadow-sm hover:shadow-md transition-colors cursor-pointer"
            style={{ background: 'var(--primary-bg-light)', color: 'var(--text-main)' }}
          >
            <Shield size={24} style={{ color: 'var(--success)', margin: '0 auto 0.5rem' }} />
            <p className="text-2xl font-bold">{userProfile && (userProfile.rewards || userProfile.points || 0)}</p>
            <p className="text-xs" style={{ color: 'var(--accent)' }}>Rewards</p>
          </button>
        </div>


        {/* Wallet Card */}
        <div className="rounded p-4 mb-4 shadow-sm border border-orange-100" style={{ background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-xl">👛</span>
              <span className="font-semibold text-base text-[#ff3f6c]">My Wallet</span>
            </div>
            <button
              onClick={onShowWalletTopup}
              className="text-xs font-semibold px-3 py-1.5 rounded-full transition-colors border border-[#ff3f6c] text-[#ff3f6c] bg-white hover:bg-[#fff8fa]"
            >
              + Top Up
            </button>
          </div>
          <p className="text-3xl font-bold mb-1 text-[#ff3f6c]">
            ₹{typeof walletBalance === 'number' ? walletBalance.toFixed(2) : '0.00'}
          </p>
          <p className="text-orange-400 text-xs mb-3">Available Balance</p>
          <button
            onClick={onShowWalletTransactions}
            className="flex items-center gap-1 text-[#ff3f6c] text-sm hover:text-[#ff3f6c] transition-colors"
          >
            View Transactions →
          </button>
        </div>

        {/* Referral Card — shown only when programme is enabled */}
        {referralInfo?.settings?.enabled && (
          <div className="rounded p-4 mb-4 shadow-sm border border-[#eaeaec]" style={{ background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)' }}>
            {/* Header */}
            <div className="flex items-center gap-2 mb-3">
              <Gift size={20} className="text-[#ff3f6c]" />
              <span className="font-semibold text-[#ff3f6c] text-base">Refer &amp; Earn</span>
            </div>

            {/* Benefits */}
            <div className="flex gap-3 mb-3">
              <div className="flex-1 bg-white rounded-lg p-2 text-center border border-[#eaeaec]">
                <p className="text-xs text-[#94969f] mb-0.5">You earn</p>
                <p className="font-bold text-[#ff3f6c] text-sm">
                  {referralInfo.settings.reward_type === 'wallet'
                    ? `₹${referralInfo.settings.reward_amount} wallet`
                    : `₹${referralInfo.settings.reward_amount} coupon`}
                </p>
              </div>
              <div className="flex-1 bg-white rounded-lg p-2 text-center border border-[#eaeaec]">
                <p className="text-xs text-[#94969f] mb-0.5">Friend earns</p>
                <p className="font-bold text-[#03a685] text-sm">
                  {referralInfo.settings.referee_reward_type === 'wallet'
                    ? `₹${referralInfo.settings.referee_reward_amount} wallet`
                    : `₹${referralInfo.settings.referee_reward_amount} coupon`}
                </p>
              </div>
              {referralInfo.settings.min_order_amount > 0 && (
                <div className="flex-1 bg-white rounded-lg p-2 text-center border border-[#eaeaec]">
                  <p className="text-xs text-[#94969f] mb-0.5">Min order</p>
                  <p className="font-bold text-[#535766] text-sm">₹{referralInfo.settings.min_order_amount}</p>
                </div>
              )}
            </div>

            {/* Your referral code */}
            <p className="text-xs text-[#ff3f6c] font-medium mb-1">Your referral code</p>
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 bg-white border border-dashed border-[#ff3f6c] rounded-lg px-3 py-2 font-mono font-bold text-[#ff3f6c] text-center tracking-widest text-lg">
                {referralInfo.referral_code || '—'}
              </div>
              <button
                onClick={() => copyReferralCode(referralInfo.referral_code)}
                className="p-2.5 bg-[#ff3f6c] text-white rounded-lg hover:bg-[#e03a60] transition-all flex-shrink-0"
                title="Copy code"
              >
                {referralCodeCopied ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>

            {/* Stats row */}
            {referralInfo.stats && (
              <div className="flex gap-3 mb-3">
                {[
                  { label: 'Total Referred', value: referralInfo.stats.total, color: 'text-[#ff3f6c]' },
                  { label: 'Pending',         value: referralInfo.stats.pending, color: 'text-yellow-600' },
                  { label: 'Rewarded',        value: referralInfo.stats.rewarded, color: 'text-[#03a685]' },
                ].map(s => (
                  <div key={s.label} className="flex-1 text-center">
                    <p className={`font-bold text-base ${s.color}`}>{s.value}</p>
                    <p className="text-xs text-[#94969f]">{s.label}</p>
                  </div>
                ))}
              </div>
            )}

            {referralInfo.stats?.total_earned > 0 && (
              <p className="text-xs text-[#03a685] font-medium mb-3">
                🎉 Total earned: ₹{parseFloat(referralInfo.stats.total_earned).toFixed(2)}
              </p>
            )}

            {/* Apply a friend's code (if not already referred) */}
            {!referralInfo.already_referred && (
              <div className="mt-1">
                <p className="text-xs text-[#94969f] mb-1">Have a friend's code? Enter it below:</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    maxLength={8}
                    placeholder="Enter code"
                    value={referralInput}
                    onChange={e => setReferralInput(e.target.value.toUpperCase())}
                    className="flex-1 border border-[#eaeaec] rounded-lg px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:border-[#ff3f6c] bg-white"
                  />
                  <button
                    onClick={handleApplyReferral}
                    disabled={applyingReferral || !referralInput.trim()}
                    className="px-4 py-2 bg-[#ff3f6c] text-white text-sm font-medium rounded-lg hover:bg-[#e03a60] disabled:opacity-50 transition-all"
                  >
                    {applyingReferral ? '...' : 'Apply'}
                  </button>
                </div>
                {referralMsg && (
                  <p className={`text-xs mt-1.5 font-medium ${referralMsg.type === 'success' ? 'text-[#03a685]' : 'text-red-500'}`}>
                    {referralMsg.text}
                  </p>
                )}
              </div>
            )}

            {/* Approval status note */}
            {!referralInfo.settings.auto_approve && (
              <p className="text-xs text-[#94969f] mt-2">
                ℹ️ Rewards are credited after admin approval.
              </p>
            )}
          </div>
        )}

        {/* Delivery Addresses */}
      <div className={`${appTheme === 'white' ? 'glass-card-white-theme' : 'bg-white'} rounded shadow-sm p-4 mb-3`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-[#282c3f] flex items-center gap-2">
            <MapPin size={20} className="text-[#ff3f6c]" />
            Delivery Addresses
          </h3>
          <button
            onClick={() => {
              setEditingAddress(null);
              setShowAddAddress(true);
            }}
            className="text-[#ff3f6c] text-sm font-medium hover:underline"
          >
            + Add New
          </button>
        </div>

        {(() => {
          const validAddrs = (deliveryAddresses || []).filter(a => a.address && a.address.trim());
          return validAddrs.length > 0 ? (
          <div className="space-y-3">
            {validAddrs.map((addr) => (
              <div
                key={addr.id}
                className={`border rounded p-3 transition-all ${
                  addr.isDefault
                    ? 'border-[#ff3f6c] bg-[#fff8fa]'
                    : 'border-[#eaeaec] hover:border-[#eaeaec]'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[#282c3f]">{addr.label}</span>
                    {addr.isDefault && (
                      <span className="bg-[#ff3f6c] text-white text-xs px-2 py-0.5 rounded-full">
                        Default
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingAddress(addr);
                        setShowAddAddress(true);
                      }}
                      className="text-[#ff3f6c] text-sm hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteAddress(addr.id)}
                      className="text-red-600 text-sm hover:underline"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                
                <p className="text-sm text-[#535766] mb-1">{addr.address}</p>
                <p className="text-sm text-[#535766]">
                  {addr.city}, {addr.state} - {addr.pincode}
                </p>
                <p className="text-sm text-[#535766] mt-1">
                  Phone: {addr.phone}
                </p>

                {!addr.isDefault && (
                  <button
                    onClick={() => handleSetDefaultAddress(addr.id)}
                    className="text-[#ff3f6c] text-sm font-medium mt-2 hover:underline"
                  >
                    Set as Default
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#94969f] text-sm text-center py-4">
            No delivery addresses added yet
          </p>
        );
        })()}
      </div>


        {/* Menu Items */}

        <div className="space-y-3">
          <button
            onClick={() => setActiveTab('orders')}
            className={`w-full rounded shadow-sm p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all ${appTheme === 'white' ? 'glass-card-white-theme' : 'bg-white'}`}
          >
            <div className="flex items-center gap-3">
              <Package size={20} className="text-[#535766]" />
              <span className="font-medium text-[#535766]">My Orders</span>
            </div>
            <ChevronRight size={20} className="text-[#94969f]" />
          </button>

          <button
            onClick={() => setActiveTab('subscriptions')}
            className={`w-full rounded shadow-sm p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all ${appTheme === 'white' ? 'glass-card-white-theme' : 'bg-white'}`}
          >
            <div className="flex items-center gap-3">
              <Bell size={20} className="text-[#03a685]" />
              <span className="font-medium text-[#535766]">My Subscriptions</span>
            </div>
            <ChevronRight size={20} className="text-[#94969f]" />
          </button>

          <button
            onClick={() => setShowFavorites(true)}
            className={`w-full rounded shadow-sm p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all ${appTheme === 'white' ? 'glass-card-white-theme' : 'bg-white'}`}
          >
            <div className="flex items-center gap-3">
              <Heart size={20} className="text-[#535766]" />
              <span className="font-medium text-[#535766]">Favorites</span>
            </div>
            <ChevronRight size={20} className="text-[#94969f]" />
          </button>

          <button 
            onClick={() => setShowNotificationsModal(true)}
            className={`w-full rounded shadow-sm p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all ${appTheme === 'white' ? 'glass-card-white-theme' : 'bg-white'}`}
          >
            <div className="flex items-center gap-3">
              <Bell size={20} className="text-[#535766]" />
              <span className="font-medium text-[#535766]">Notifications</span>
            </div>
            <ChevronRight size={20} className="text-[#94969f]" />
          </button>

          <button 
            onClick={() => setShowPaymentMethods(true)}
            className={`w-full rounded shadow-sm p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all ${appTheme === 'white' ? 'glass-card-white-theme' : 'bg-white'}`}
          >
            <div className="flex items-center gap-3">
              <Package size={20} className="text-[#535766]" />
              <span className="font-medium text-[#535766]">Payment Methods</span>
            </div>
            <ChevronRight size={20} className="text-[#94969f]" />
          </button>
          {appSettings.securityAlertsEnabled  && (
          <button 
            onClick={() => setShowSecuritySettings(true)}
            className={`w-full rounded shadow-sm p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all ${appTheme === 'white' ? 'glass-card-white-theme' : 'bg-white'}`}
          >
            <div className="flex items-center gap-3">
              <Shield size={20} className="text-[#ff3f6c]" />
              <span className="font-medium text-[#535766]">Security & Privacy</span>
            </div>
            <ChevronRight size={20} className="text-[#94969f]" />
          </button>
          )}
    
          {userProfile?.is_admin && (
            <>
              <button
                onClick={() => setShowPWAScreen(true)}
                className="w-full bg-[#22c55e] rounded shadow-md p-4 text-left flex items-center justify-between hover:shadow-lg transition-all"
              >
                <div className="flex items-center gap-3">
                  <Smartphone size={20} className="text-white" />
                  <div>
                    <span className="font-medium text-white block">App Settings (PWA)</span>
                    <span className="text-xs text-emerald-100">Install, offline & notifications</span>
                  </div>
                </div>
                <ChevronRight size={20} className="text-white" />
              </button>

              <button
                onClick={() => setShowQRScreen(true)}
                className="w-full bg-[#22c55e] rounded shadow-md p-4 text-left flex items-center justify-between hover:shadow-lg transition-all"
              >
                <div className="flex items-center gap-3">
                  <QrCode size={20} className="text-white" />
                  <div>
                    <span className="font-medium text-white block">QR Code</span>
                    <span className="text-xs text-emerald-100">Share our website</span>
                  </div>
                </div>
                <ChevronRight size={20} className="text-white" />
              </button>
            </>
          )}
          
          <button
            onClick={() => setActiveTab('help')}
            className={`w-full rounded shadow-sm p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-all ${appTheme === 'white' ? 'glass-card-white-theme' : 'bg-white'}`}> 
            <div className="flex items-center gap-3">
              <MessageCircle size={20} className="text-[#535766]" />
              <span className="font-medium text-[#535766]">Help & Support</span>
            </div>
            <ChevronRight size={20} className="text-[#94969f]" />
          </button>

        </div>

        {userProfile?.is_admin && (
        <div className={`${appTheme === 'white' ? 'glass-card-white-theme' : 'bg-white'} rounded shadow-sm p-4 mt-3`}>
          <div className="flex items-center gap-2 mb-1">
            <Palette size={20} className="text-[#ff3f6c]" />
            <h3 className="font-semibold text-[#282c3f]">Appearance</h3>
          </div>
          <p className="text-sm text-[#94969f] mb-4">Choose the look you want. Your selection is saved on this device.</p>

          <div className="space-y-3">
            {themeOptions.map((themeOption) => {
              const ThemeIcon = themeOption.icon;
              const isSelected = appTheme === themeOption.id;

              return (
                <button
                  key={themeOption.id}
                  type="button"
                  onClick={() => onChangeTheme && onChangeTheme(themeOption.id)}
                  className={`w-full rounded border p-3 text-left transition-all ${
                    isSelected
                      ? 'border-[#ff3f6c] ring-2 ring-blue-100 shadow-md'
                      : 'border-[#eaeaec] hover:border-[#eaeaec] hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-11 h-11 rounded bg-gradient-to-br ${themeOption.previewClass} flex items-center justify-center shadow-sm flex-shrink-0`}>
                        <ThemeIcon size={19} className={themeOption.previewIconClass} />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-[#282c3f]">{themeOption.label}</p>
                        <p className="text-sm text-[#94969f]">{themeOption.description}</p>
                      </div>
                    </div>

                    {isSelected && (
                      <span className="w-8 h-8 rounded-full bg-[#ff3f6c] text-white flex items-center justify-center flex-shrink-0">
                        <Check size={16} />
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-4 gap-2">
                    <span className={`h-3 rounded-full bg-gradient-to-r ${themeOption.previewClass}`}></span>
                    <span className="h-3 rounded-full bg-white border border-[#eaeaec]"></span>
                    <span className="h-3 rounded-full bg-gray-100 border border-[#eaeaec]"></span>
                    <span className="h-3 rounded-full bg-[#fff8fa] border border-[#eaeaec]"></span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        )}

        {/* App Info */}
        <div className="mt-6 text-center">
          <p className="text-xs text-[#94969f]">Version {appSettings?.version || '1.0.0'}</p>
          <p className="text-xs text-[#94969f] mt-1">© {new Date().getFullYear()} {appSettings?.appName || 'DealStore App'}</p>
        </div>
      </div>
    </div>
  );
};

export default ProfileScreen;